import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface Actual {
  id: string;
  projectId: string;
  costCodeId: string;
  costCode: string;
  costCodeName: string;
  amount: number;
  description: string | null;
  date: string;
  commitmentId: string | null;
  createdAt: string;
}

export interface CreateActualData {
  costCodeId: string;
  amount: number;
  description?: string;
  date: string;
  commitmentId?: string;
}

export interface UpdateActualData {
  amount?: number;
  description?: string;
  date?: string;
  commitmentId?: string;
}

interface ActualsResponse {
  actuals: Actual[];
  summary: {
    totalActuals: number;
    totalAmount: number;
    byCostCode: {
      costCode: string;
      costCodeName: string;
      total: number;
    }[];
  };
}

interface ActualResponse {
  actual: Actual;
}

// Get all actuals for a project
export function useActuals(
  projectId: string,
  options?: { costCodeId?: string; startDate?: string; endDate?: string }
) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "actuals", options],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const params = new URLSearchParams();
      if (options?.costCodeId) params.set("costCodeId", options.costCodeId);
      if (options?.startDate) params.set("startDate", options.startDate);
      if (options?.endDate) params.set("endDate", options.endDate);
      const queryString = params.toString();
      const url = `/api/projects/${projectId}/actuals${queryString ? `?${queryString}` : ""}`;

      return fetchApi<ActualsResponse>(url, { headers });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get a single actual
export function useActual(projectId: string, actualId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "actuals", actualId],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ActualResponse>(
        `/api/projects/${projectId}/actuals/${actualId}`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId && !!actualId,
    staleTime: 1000 * 60 * 5,
  });
}

// Create a new actual
export function useCreateActual(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateActualData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ActualResponse>(`/api/projects/${projectId}/actuals`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "actuals"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// Update an actual
export function useUpdateActual(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateActualData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ActualResponse>(`/api/projects/${projectId}/actuals/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "actuals"] });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "actuals", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// Delete an actual
export function useDeleteActual(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/projects/${projectId}/actuals/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "actuals"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}
